import type { Booking, VenueConfig } from "../../common/jsonRpcTypes";

export function isAuthenticationPayment(depositTypePayment: string | boolean): boolean {
  return depositTypePayment !== true && depositTypePayment !== "true";
}

/**
 * The message Collins returns when a credit-funded card is refused for a booking
 * type with credit card payments disabled (`CreditCardsDisabledForVenuePaymentException`).
 * Matched verbatim so the guest is shown the specific reason for the refusal
 * rather than the generic payment error.
 */
export const CREDIT_CARDS_DISABLED_ERROR = "Credit card payments have been disabled for this type of booking.";

/**
 * Stripe reports card funding as `credit`, `debit`, `prepaid` or `unknown`. Only a
 * confirmed `credit` funding is refused — `unknown` is left to the Collins
 * validation, which cancels the payment intent before any money is taken.
 */
export const CREDIT_CARD_FUNDING = "credit";

/**
 * Whether the venue refuses credit-funded cards for the given booking type.
 * Collins enforces this server-side; reading it here lets the guest be warned
 * before entering card details, and the card be refused before it is charged.
 *
 * Used by the /book flow, which has a venue config and a chosen booking type but
 * no persisted booking yet. Flows that start from an existing booking read the
 * flag off the booking instead — see `isCreditCardPaymentDisabledForBooking`.
 */
export function isCreditCardPaymentDisabled(
  venueConfig: VenueConfig | null | undefined,
  bookingTypeId: string | null | undefined,
): boolean {
  if (!bookingTypeId) return false;

  const bookingType = venueConfig?.bookingTypes?.find((type) => type.id === bookingTypeId);

  return bookingType?.disable_credit_card_payments === true;
}

/**
 * The same rule for flows that load an existing booking (secure your booking,
 * place your order, manage your order). Those have no venue config, so Collins
 * resolves the booking type for us and the flag arrives on the booking itself.
 */
export function isCreditCardPaymentDisabledForBooking(
  booking: Pick<Booking, "disableCreditCardPayments"> | null | undefined,
): boolean {
  return booking?.disableCreditCardPayments === true;
}
